import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, UserCheckIcon, UserXIcon, EyeIcon, SettingsIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon, AlertCircleIcon, BuildingIcon, ShieldIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
// Mock merchant data
const merchantsData = [{
  id: 'M001',
  name: 'Global Electronics',
  businessType: 'Electronics',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-28T10:30:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: true,
    multiMerchant: true
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 2
  }, {
    id: 'manager',
    name: 'Manager',
    count: 3
  }, {
    id: 'staff',
    name: 'Staff',
    count: 5
  }]
}, {
  id: 'M002',
  name: 'City Retailers',
  businessType: 'Retail',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-27T14:45:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: false,
    multiMerchant: false
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 1
  }, {
    id: 'manager',
    name: 'Manager',
    count: 2
  }]
}, {
  id: 'M003',
  name: 'Gourmet Foods',
  businessType: 'Food & Beverage',
  tier: 'Standard',
  status: 'active',
  portalAccess: false,
  lastLogin: null,
  features: {
    transactions: false,
    qrGeneration: false,
    reports: false,
    profileManagement: false,
    campaigns: false,
    multiMerchant: false
  },
  roles: []
}, {
  id: 'M004',
  name: 'Tech Solutions Inc',
  businessType: 'Technology',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-25T09:15:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: true,
    multiMerchant: false
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 1
  }, {
    id: 'manager',
    name: 'Manager',
    count: 4
  }, {
    id: 'staff',
    name: 'Staff',
    count: 8
  }]
}, {
  id: 'M005',
  name: 'Fashion Forward',
  businessType: 'Apparel',
  tier: 'Standard',
  status: 'inactive',
  portalAccess: false,
  lastLogin: null,
  features: {
    transactions: false,
    qrGeneration: false,
    reports: false,
    profileManagement: false,
    campaigns: false,
    multiMerchant: false
  },
  roles: []
}, {
  id: 'M006',
  name: 'Metro Hospitality',
  businessType: 'Hospitality',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-26T11:30:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: false,
    multiMerchant: true
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 2
  }, {
    id: 'manager',
    name: 'Manager',
    count: 5
  }]
}];
// Define feature descriptions
const featureDescriptions = {
  transactions: 'View and manage transaction history',
  qrGeneration: 'Generate and manage QR codes for payments',
  reports: 'Access detailed reports and analytics',
  profileManagement: 'Update merchant profile and settings',
  campaigns: 'View and participate in marketing campaigns',
  multiMerchant: 'Manage multiple merchant accounts'
};
// Define role permissions
const rolePermissions = {
  admin: {
    name: 'Admin',
    description: 'Full access to all features',
    permissions: ['View transactions', 'Generate QR codes', 'View reports', 'Manage profile', 'View campaigns', 'Manage users', 'Configure settings']
  },
  manager: {
    name: 'Manager',
    description: 'Access to most features with some restrictions',
    permissions: ['View transactions', 'Generate QR codes', 'View reports', 'View profile', 'View campaigns']
  },
  staff: {
    name: 'Staff',
    description: 'Limited access to basic features',
    permissions: ['View transactions', 'Generate QR codes']
  }
};
interface Merchant {
  id: string;
  name: string;
  businessType: string;
  tier: string;
  status: string;
  portalAccess: boolean;
  lastLogin: string | null;
  features: {
    transactions: boolean;
    qrGeneration: boolean;
    reports: boolean;
    profileManagement: boolean;
    campaigns: boolean;
    multiMerchant: boolean;
  };
  roles: Array<{
    id: string;
    name: string;
    count: number;
  }>;
}
const MerchantAccessControl: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>(merchantsData);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>(merchantsData);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [tierFilter, setTierFilter] = useState('');
  const [accessFilter, setAccessFilter] = useState('');
  // Modal states
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Edit states
  const [editedFeatures, setEditedFeatures] = useState<Record<string, boolean>>({});
  const [bulkEnableDisable, setBulkEnableDisable] = useState<'enable' | 'disable' | ''>('');
  // Apply filters
  const applyFilters = () => {
    let result = merchants;
    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(merchant => merchant.name.toLowerCase().includes(term) || merchant.id.toLowerCase().includes(term) || merchant.businessType.toLowerCase().includes(term));
    }
    // Tier filter
    if (tierFilter) {
      result = result.filter(merchant => merchant.tier === tierFilter);
    }
    // Access filter
    if (accessFilter === 'enabled') {
      result = result.filter(merchant => merchant.portalAccess);
    } else if (accessFilter === 'disabled') {
      result = result.filter(merchant => !merchant.portalAccess);
    }
    setFilteredMerchants(result);
  };
  // Reset filters
  const resetFilters = () => {
    setTierFilter('');
    setAccessFilter('');
    setSearchTerm('');
  };
  // Toggle portal access
  const togglePortalAccess = (merchant: Merchant) => {
    if (merchant.portalAccess) {
      // If disabling access, show confirmation
      setSelectedMerchant(merchant);
      setShowConfirmModal(true);
    } else {
      // If enabling access, do it directly
      updateMerchantAccess(merchant, true);
    }
  };
  // Update merchant access
  const updateMerchantAccess = (merchant: Merchant, access: boolean) => {
    const updatedMerchants = merchants.map(m => {
      if (m.id === merchant.id) {
        return {
          ...m,
          portalAccess: access,
          // If enabling access, set default features
          features: access ? {
            transactions: true,
            qrGeneration: true,
            reports: true,
            profileManagement: true,
            campaigns: false,
            multiMerchant: false
          } : {
            transactions: false,
            qrGeneration: false,
            reports: false,
            profileManagement: false,
            campaigns: false,
            multiMerchant: false
          },
          // If enabling access, add default admin role
          roles: access ? [{
            id: 'admin',
            name: 'Admin',
            count: 1
          }] : []
        };
      }
      return m;
    });
    setMerchants(updatedMerchants);
    applyFilters();
    setShowConfirmModal(false);
  };
  // Open features modal
  const openFeaturesModal = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setEditedFeatures(merchant.features);
    setShowFeaturesModal(true);
  };
  // Open roles modal
  const openRolesModal = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowRolesModal(true);
  };
  // Save features
  const saveFeatures = () => {
    if (!selectedMerchant) return;
    const updatedMerchants = merchants.map(m => {
      if (m.id === selectedMerchant.id) {
        return {
          ...m,
          features: editedFeatures as typeof m.features
        };
      }
      return m;
    });
    setMerchants(updatedMerchants);
    applyFilters();
    setShowFeaturesModal(false);
  };
  // Handle feature change
  const handleFeatureChange = (feature: string, value: boolean) => {
    setEditedFeatures(prev => ({
      ...prev,
      [feature]: value
    }));
  };
  // Bulk enable/disable features
  const handleBulkFeatureChange = () => {
    if (!bulkEnableDisable) return;
    const allEnabled = bulkEnableDisable === 'enable';
    setEditedFeatures({
      transactions: allEnabled,
      qrGeneration: allEnabled,
      reports: allEnabled,
      profileManagement: allEnabled,
      campaigns: allEnabled,
      multiMerchant: allEnabled
    });
    setBulkEnableDisable('');
  };
  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Effect to apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [searchTerm, tierFilter, accessFilter, merchants]);
  return <div className="space-y-6" data-id="element-300">
      <div data-id="element-301">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-302">
          Merchant Portal Access Control
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-303">
          Manage merchant access to the self-service portal
        </p>
      </div>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-304">
        <div className="relative flex-1" data-id="element-305">
          <input type="text" placeholder="Search merchants..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-306" />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-307">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-308" />
          </div>
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-309">
          <FilterIcon className="h-4 w-4 mr-2" data-id="element-310" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>
      {/* Advanced Filters */}
      {showFilters && <Card data-id="element-311">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-312">
            <div data-id="element-313">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-314">
                Merchant Tier
              </label>
              <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-315">
                <option value="" data-id="element-316">All Tiers</option>
                <option value="Premium" data-id="element-317">Premium</option>
                <option value="Standard" data-id="element-318">Standard</option>
                <option value="Basic" data-id="element-319">Basic</option>
              </select>
            </div>
            <div data-id="element-320">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-321">
                Portal Access
              </label>
              <select value={accessFilter} onChange={e => setAccessFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-322">
                <option value="" data-id="element-323">All</option>
                <option value="enabled" data-id="element-324">Enabled</option>
                <option value="disabled" data-id="element-325">Disabled</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-326">
            <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-327">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-328">
              Apply Filters
            </button>
          </div>
        </Card>}
      {/* Merchants Table */}
      <Card data-id="element-329">
        <div className="mb-4 flex items-center justify-between" data-id="element-330">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-331">
            Showing {filteredMerchants.length} merchants
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-332">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-333">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-334">
              <tr data-id="element-335">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-336">
                  Merchant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-337">
                  Tier
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-338">
                  Portal Access
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-339">
                  Last Login
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-340">
                  Features
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-341">
                  Users
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-342">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-343">
              {filteredMerchants.map(merchant => <tr key={merchant.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-344">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-345">
                    <div className="flex items-center" data-id="element-346">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-347">
                        <BuildingIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-348" />
                      </div>
                      <div className="ml-4" data-id="element-349">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-350">
                          {merchant.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-351">
                          ID: {merchant.id} • {merchant.businessType}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-352">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${merchant.tier === 'Premium' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : merchant.tier === 'Standard' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-353">
                      {merchant.tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-354">
                    <div className="flex items-center" data-id="element-355">
                      {merchant.portalAccess ? <StatusBadge status="success" text="Enabled" data-id="element-356" /> : <StatusBadge status="danger" text="Disabled" data-id="element-357" />}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-358">
                    {formatDate(merchant.lastLogin)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-359">
                    {merchant.portalAccess ? <button onClick={() => openFeaturesModal(merchant)} className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-360">
                        <SettingsIcon className="h-4 w-4 mr-1" data-id="element-361" />
                        Configure
                      </button> : <span className="text-gray-400 dark:text-gray-500" data-id="element-362">
                        Not available
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-363">
                    {merchant.portalAccess ? <button onClick={() => openRolesModal(merchant)} className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-364">
                        {merchant.roles.reduce((sum, role) => sum + role.count, 0)}{' '}
                        users
                      </button> : <span className="text-gray-400 dark:text-gray-500" data-id="element-365">
                        No users
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-366">
                    <button onClick={() => togglePortalAccess(merchant)} className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${merchant.portalAccess ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'}`} data-id="element-367">
                      {merchant.portalAccess ? <>
                          <UserXIcon className="h-3.5 w-3.5 mr-1" data-id="element-368" />
                          Disable Access
                        </> : <>
                          <UserCheckIcon className="h-3.5 w-3.5 mr-1" data-id="element-369" />
                          Enable Access
                        </>}
                    </button>
                  </td>
                </tr>)}
              {filteredMerchants.length === 0 && <tr data-id="element-370">
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-371">
                    No merchants found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Features Configuration Modal */}
      <Modal isOpen={showFeaturesModal} onClose={() => setShowFeaturesModal(false)} title="Configure Portal Features" size="lg" data-id="element-372">
        {selectedMerchant && <div className="space-y-6" data-id="element-373">
            <div className="flex items-center" data-id="element-374">
              <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-375">
                <BuildingIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-376" />
              </div>
              <div className="ml-4" data-id="element-377">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-378">
                  {selectedMerchant.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-379">
                  {selectedMerchant.tier} Tier • ID: {selectedMerchant.id}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-3" data-id="element-380">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-381">
                Enable or disable features for this merchant
              </h4>
              <div className="flex space-x-2" data-id="element-382">
                <button onClick={() => {
              setBulkEnableDisable('enable');
              handleBulkFeatureChange();
            }} className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-xs font-medium" data-id="element-383">
                  Enable All
                </button>
                <button onClick={() => {
              setBulkEnableDisable('disable');
              handleBulkFeatureChange();
            }} className="px-3 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-md text-xs font-medium" data-id="element-384">
                  Disable All
                </button>
              </div>
            </div>
            <div className="space-y-4" data-id="element-385">
              {Object.entries(editedFeatures).map(([feature, enabled]) => <div key={feature} className="flex items-center justify-between" data-id="element-386">
                  <div data-id="element-387">
                    <div className="flex items-center" data-id="element-388">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-389">
                        {feature.charAt(0).toUpperCase() + feature.slice(1).replace(/([A-Z])/g, ' $1')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-390">
                      {featureDescriptions[feature as keyof typeof featureDescriptions]}
                    </p>
                  </div>
                  <div className="flex items-center" data-id="element-391">
                    <button onClick={() => handleFeatureChange(feature, true)} className={`mr-2 px-3 py-1 rounded-md text-xs font-medium ${enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-392">
                      Enable
                    </button>
                    <button onClick={() => handleFeatureChange(feature, false)} className={`px-3 py-1 rounded-md text-xs font-medium ${!enabled ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-393">
                      Disable
                    </button>
                  </div>
                </div>)}
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-394">
              <button onClick={() => setShowFeaturesModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-395">
                Cancel
              </button>
              <button onClick={saveFeatures} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center" data-id="element-396">
                <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-397" />
                Save Changes
              </button>
            </div>
          </div>}
      </Modal>
      {/* Roles Modal */}
      <Modal isOpen={showRolesModal} onClose={() => setShowRolesModal(false)} title="Manage User Roles" size="lg" data-id="element-398">
        {selectedMerchant && <div className="space-y-6" data-id="element-399">
            <div className="flex items-center" data-id="element-400">
              <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-401">
                <BuildingIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-402" />
              </div>
              <div className="ml-4" data-id="element-403">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-404">
                  {selectedMerchant.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-405">
                  {selectedMerchant.tier} Tier • ID: {selectedMerchant.id}
                </p>
              </div>
            </div>
            <div className="border-b border-gray-200 dark:border-gray-700 pb-3" data-id="element-406">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-407">
                User roles and permissions
              </h4>
            </div>
            <div className="space-y-6" data-id="element-408">
              {selectedMerchant.roles.length > 0 ? selectedMerchant.roles.map(role => <div key={role.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-409">
                    <div className="flex items-center justify-between mb-3" data-id="element-410">
                      <div className="flex items-center" data-id="element-411">
                        <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30 mr-3" data-id="element-412">
                          <ShieldIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-413" />
                        </div>
                        <div data-id="element-414">
                          <h5 className="text-base font-medium text-gray-900 dark:text-white" data-id="element-415">
                            {role.name}
                          </h5>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-416">
                            {role.count} {role.count === 1 ? 'user' : 'users'}{' '}
                            with this role
                          </p>
                        </div>
                      </div>
                      <a href="#" className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300" data-id="element-417">
                        Manage Users
                      </a>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-418">
                      {rolePermissions[role.id as keyof typeof rolePermissions]?.description || 'Custom role'}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-419">
                      {rolePermissions[role.id as keyof typeof rolePermissions]?.permissions.map((permission, index) => <div key={index} className="flex items-center" data-id="element-420">
                          <CheckIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-421" />
                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-422">
                            {permission}
                          </span>
                        </div>)}
                    </div>
                  </div>) : <div className="text-center py-6 text-gray-500 dark:text-gray-400" data-id="element-423">
                  No user roles defined for this merchant.
                </div>}
              <div className="text-center" data-id="element-424">
                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-425">
                  Add New Role
                </button>
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-426">
              <button onClick={() => setShowRolesModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-427">
                Close
              </button>
            </div>
          </div>}
      </Modal>
      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Access Removal" data-id="element-428">
        {selectedMerchant && <div className="space-y-4" data-id="element-429">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4" data-id="element-430">
              <div className="flex" data-id="element-431">
                <div className="flex-shrink-0" data-id="element-432">
                  <AlertCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" data-id="element-433" />
                </div>
                <div className="ml-3" data-id="element-434">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-435">
                    Warning: This action will disable portal access
                  </h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-200" data-id="element-436">
                    <p data-id="element-437">
                      You are about to disable portal access for{' '}
                      <strong data-id="element-438">{selectedMerchant.name}</strong>. This will:
                    </p>
                    <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-439">
                      <li data-id="element-440">Immediately revoke access for all users</li>
                      <li data-id="element-441">Disable all features and configurations</li>
                      <li data-id="element-442">Require reconfiguration if access is re-enabled</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-gray-700 dark:text-gray-300" data-id="element-443">
              Are you sure you want to continue?
            </p>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-444">
              <button onClick={() => setShowConfirmModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-445">
                Cancel
              </button>
              <button onClick={() => updateMerchantAccess(selectedMerchant, false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-446">
                Disable Access
              </button>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default MerchantAccessControl;